import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

// Types for the hook configuration
export interface SubmitToastOptions {
  loading?: string;
  success?: string;
  error?: string;
  duration?: number;
  successDuration?: number;
  errorDuration?: number;
  id?: string;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';
  dismissible?: boolean;
  icon?: any;
}

export type AsyncFunction<T = void> = () => Promise<T>;
export type SyncFunction<T = void> = () => T;

export interface UseSubmitToastReturn {
  executeWithToast: <T = void>(
    fn: AsyncFunction<T> | SyncFunction<T>,
    options?: SubmitToastOptions
  ) => Promise<T | undefined>;
  executeWithCustomMessages: <T = void>(
    fn: AsyncFunction<T> | SyncFunction<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    },
    options?: Omit<SubmitToastOptions, 'loading' | 'success' | 'error'>
  ) => Promise<T | undefined>;
  showSuccess: (message: string, options?: Pick<SubmitToastOptions, 'duration' | 'id' | 'position'>) => void;
  showError: (message: string, options?: Pick<SubmitToastOptions, 'duration' | 'id' | 'position'>) => void;
  showInfo: (message: string, options?: Pick<SubmitToastOptions, 'duration' | 'id' | 'position'>) => void;
  showLoading: (message: string, options?: Pick<SubmitToastOptions, 'id' | 'position'>) => string;
  dismissToast: (id?: string) => void;
}

// Default messages with internationalization support
const getDefaultMessages = (t: (key: string, fallback?: string) => string) => ({
  loading: t('common.status.loading', 'Loading...'),
  saving: t('common.status.saving', 'Saving...'),
  success: t('common.messages.operationSuccessful', 'Operation successful'),
  error: t('common.messages.operationFailed', 'Operation failed'),
  saveSuccess: t('common.messages.saveSuccessful', 'Successfully saved'),
  saveError: t('common.messages.saveError', 'Failed to save'),
  deleteSuccess: t('interactions.delete_success', 'Deleted successfully'),
  deleteError: t('interactions.delete_error', 'Failed to delete'),
  createSuccess: t('interactions.interaction_created', 'Created successfully'),
  createError: t('forms.validation.failedToCreate', 'Failed to create'),
  updateSuccess: t('interactions.interaction_updated', 'Updated successfully'),
  updateError: t('forms.validation.failedToCreate', 'Failed to update'),
  networkError: t('common.messages.operationFailed', 'Network error. Please try again.'),
  unexpectedError: t('forms.validation.unexpectedError', 'An unexpected error occurred. Please try again.'),
});

export const useSubmitToast = (): UseSubmitToastReturn => {
  const { t } = useLanguage();
  const activeToastsRef = useRef<Set<string>>(new Set());

  const defaultMessages = getDefaultMessages(t);

  // Smart message detection based on common action patterns
  const detectActionType = (errorMessage?: string): keyof typeof defaultMessages => {
    if (!errorMessage) return 'error';

    const lowerError = errorMessage.toLowerCase();

    if (lowerError.includes('save') || lowerError.includes('saving')) {
      return 'saveError';
    }
    if (lowerError.includes('delete') || lowerError.includes('removing')) {
      return 'deleteError';
    }
    if (lowerError.includes('create') || lowerError.includes('creating')) {
      return 'createError';
    }
    if (lowerError.includes('update') || lowerError.includes('updating')) {
      return 'updateError';
    }
    if (lowerError.includes('network') || lowerError.includes('fetch') || lowerError.includes('connection')) {
      return 'networkError';
    }

    return 'error';
  };

  const showSuccess = useCallback((
    message: string,
    options: Pick<SubmitToastOptions, 'duration' | 'id' | 'position'> = {}
  ) => {
    const toastId = toast.success(message, {
      id: options.id,
      duration: options.duration ?? 3000,
      position: options.position ?? 'top-center',
    });
    return toastId;
  }, []);

  const showError = useCallback((
    message: string,
    options: Pick<SubmitToastOptions, 'duration' | 'id' | 'position'> = {}
  ) => {
    const toastId = toast.error(message, {
      id: options.id,
      duration: options.duration ?? 5000,
      position: options.position ?? 'top-center',
    });
    return toastId;
  }, []);

  const showInfo = useCallback((
    message: string,
    options: Pick<SubmitToastOptions, 'duration' | 'id' | 'position'> = {}
  ) => {
    const toastId = toast.info(message, {
      id: options.id,
      duration: options.duration ?? 4000,
      position: options.position ?? 'top-center',
    });
    return toastId;
  }, []);

  const showLoading = useCallback((
    message: string,
    options: Pick<SubmitToastOptions, 'id' | 'position'> = {}
  ) => {
    const toastId = toast.loading(message, {
      id: options.id,
      position: options.position ?? 'top-center',
    });

    if (toastId) {
      activeToastsRef.current.add(toastId);
    }

    return toastId;
  }, []);

  const dismissToast = useCallback((id?: string) => {
    if (id) {
      toast.dismiss(id);
      activeToastsRef.current.delete(id);
    } else {
      toast.dismiss();
      activeToastsRef.current.clear();
    }
  }, []);

  const executeWithToast = useCallback(async <T = void>(
    fn: AsyncFunction<T> | SyncFunction<T>,
    options: SubmitToastOptions = {}
  ): Promise<T | undefined> => {
    const {
      loading,
      success,
      error,
      duration,
      successDuration,
      errorDuration,
      id,
      position = 'top-center',
      dismissible = true,
      icon
    } = options;

    // Generate a unique toast ID if not provided
    const toastId = id || `submit-toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Show loading toast
    const loadingMessage = loading || defaultMessages.loading;
    showLoading(loadingMessage, { id: toastId, position });

    try {
      // Execute the function
      const result = await fn();

      // Show success toast
      const successMessage = success || defaultMessages.success;
      toast.success(successMessage, {
        id: toastId,
        duration: successDuration ?? duration ?? 3000,
        position,
        dismissible,
        icon,
      });

      activeToastsRef.current.delete(toastId);
      return result;

    } catch (err) {
      // Handle error
      const errorMessage = err instanceof Error ? err.message : String(err);
      const actionType = detectActionType(errorMessage);
      const errorFallbackMessage = defaultMessages[actionType];

      const displayMessage = error || errorFallbackMessage;

      toast.error(displayMessage, {
        id: toastId,
        duration: errorDuration ?? duration ?? 5000,
        position,
        dismissible,
        icon,
      });

      activeToastsRef.current.delete(toastId);
      throw err; // Re-throw the error for the caller to handle if needed

    } finally {
      // Clean up from active toasts
      activeToastsRef.current.delete(toastId);
    }
  }, [defaultMessages, showLoading]);

  const executeWithCustomMessages = useCallback(async <T = void>(
    fn: AsyncFunction<T> | SyncFunction<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    },
    options: Omit<SubmitToastOptions, 'loading' | 'success' | 'error'> = {}
  ): Promise<T | undefined> => {
    return executeWithToast(fn, {
      ...options,
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
    });
  }, [executeWithToast]);

  // Cleanup function for when component unmounts
  const cleanup = useCallback(() => {
    activeToastsRef.current.forEach(toastId => {
      toast.dismiss(toastId);
    });
    activeToastsRef.current.clear();
  }, []);

  // Auto-cleanup on unmount
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', cleanup);
  }

  return {
    executeWithToast,
    executeWithCustomMessages,
    showSuccess,
    showError,
    showInfo,
    showLoading,
    dismissToast,
  };
};

// Utility hook for common form submission patterns
export const useFormSubmitToast = () => {
  const submitToast = useSubmitToast();
  const { t } = useLanguage();

  const saveForm = useCallback(async <T = void>(
    saveFn: () => Promise<T>,
    options?: { successMessage?: string; entityName?: string }
  ) => {
    const entityName = options?.entityName || t('common.buttons.save', 'data');
    const successMessage = options?.successMessage ||
      t('common.messages.saveSuccessful', 'Successfully saved') + ` ${entityName}`;

    return submitToast.executeWithToast(saveFn, {
      loading: t('common.status.saving', 'Saving...'),
      success: successMessage,
    });
  }, [submitToast, t]);

  const deleteItem = useCallback(async <T = void>(
    deleteFn: () => Promise<T>,
    options?: { successMessage?: string; itemName?: string }
  ) => {
    const itemName = options?.itemName || t('common.buttons.delete', 'item');
    const successMessage = options?.successMessage ||
      t('interactions.delete_success', 'Successfully deleted') + ` ${itemName}`;

    return submitToast.executeWithToast(deleteFn, {
      loading: t('common.status.loading', 'Deleting...'),
      success: successMessage,
    });
  }, [submitToast, t]);

  const createItem = useCallback(async <T = void>(
    createFn: () => Promise<T>,
    options?: { successMessage?: string; entityName?: string }
  ) => {
    const entityName = options?.entityName || t('common.buttons.create', 'item');
    const successMessage = options?.successMessage ||
      t('interactions.interaction_created', 'Successfully created') + ` ${entityName}`;

    return submitToast.executeWithToast(createFn, {
      loading: t('common.status.loading', 'Creating...'),
      success: successMessage,
    });
  }, [submitToast, t]);

  const updateItem = useCallback(async <T = void>(
    updateFn: () => Promise<T>,
    options?: { successMessage?: string; entityName?: string }
  ) => {
    const entityName = options?.entityName || t('common.buttons.update', 'item');
    const successMessage = options?.successMessage ||
      t('interactions.interaction_updated', 'Successfully updated') + ` ${entityName}`;

    return submitToast.executeWithToast(updateFn, {
      loading: t('common.status.saving', 'Updating...'),
      success: successMessage,
    });
  }, [submitToast, t]);

  return {
    ...submitToast,
    saveForm,
    deleteItem,
    createItem,
    updateItem,
  };
};

export default useSubmitToast;